import { Suspense, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, m as motion } from "framer-motion";
import { Menu, X, MessageCircle, ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { Toaster } from "sonner";
import { Logo } from "./Logo";
import { SmoothScroll } from "./SmoothScroll";
import { usePerformance } from "../lib/performance";
import { NAV, CONTACT, WA_LINK, BRAND } from "../lib/site";

const WhatsAppFloat = () => (
  <a
    href={WA_LINK()}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-float-btn"
    aria-label="Chat with our export desk on WhatsApp"
    className="fixed bottom-5 right-5 z-50 group"
  >
    <span className="absolute inset-0 rounded-full bg-[#25D366] wa-ping" />
    <span className="relative flex items-center gap-2 bg-[#25D366] text-white rounded-full px-4 py-4 sm:px-5 shadow-[0_16px_40px_-12px_rgba(37,211,102,0.6)] group-hover:scale-105 transition-transform duration-300">
      <MessageCircle className="h-6 w-6" strokeWidth={2.2} />
      <span className="hidden sm:block text-sm font-semibold pr-1">Chat with Export Desk</span>
    </span>
  </a>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const { reducedEffects } = usePerformance();
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header className="sticky top-0 z-40 bg-cream/90 backdrop-blur-md border-b border-parchment">
        <div className="max-w-screen-2xl mx-auto px-5 lg:px-10 h-[72px] flex items-center justify-between gap-3 sm:gap-6">
          <Logo />
          <nav className="hidden xl:flex items-center gap-4 whitespace-nowrap">
            {NAV.map((l) => (
              <NavLink
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-mono text-[10px] 2xl:text-[11px] uppercase tracking-[0.12em] transition-colors duration-200 ${isActive ? "text-tang" : "text-ink/70 hover:text-forest"}`
                }
              >
                {l.name}
              </NavLink>
            ))}
          </nav>
          <div className="flex shrink-0 items-center gap-3">
            <Link
              to="/request-quote"
              data-testid="header-quote-btn"
              className="hidden sm:inline-flex shrink-0 whitespace-nowrap items-center gap-2 bg-forest text-cream px-5 py-3 text-sm font-semibold hover:bg-forest-deep hover:-translate-y-0.5 transition-all duration-300"
            >
              Request a Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              aria-expanded={open}
              className="xl:hidden p-2.5 border border-parchment text-forest bg-white"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={reducedEffects ? false : { opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: reducedEffects ? 0 : 0.25, ease: "easeOut" }}
            className="fixed inset-x-0 top-[72px] bottom-0 z-30 bg-forest-deep text-cream xl:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-10 flex flex-col gap-1">
              {NAV.map((l, i) => (
                <motion.div key={l.href} initial={reducedEffects ? false : { opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: reducedEffects ? 0 : 0.25, delay: reducedEffects ? 0 : 0.05 * i }}>
                  <NavLink to={l.href} data-testid={`mobile-nav-${l.name.toLowerCase().replace(/\s+/g, "-")}`} className="block font-serif text-3xl font-semibold py-3 border-b border-cream/10">
                    {l.name}
                  </NavLink>
                </motion.div>
              ))}
              <Link to="/track" data-testid="mobile-nav-track" className="block font-serif text-3xl font-semibold py-3 border-b border-cream/10 text-leaf">Track Shipment</Link>
              <Link to="/request-quote" data-testid="mobile-quote-btn" className="mt-6 inline-flex items-center justify-center gap-2 bg-tang text-white px-8 py-4 font-semibold">
                Request a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Footer = () => (
  <footer className="bg-forest-ink text-cream relative overflow-hidden">
    <p className="select-none pointer-events-none font-serif font-bold uppercase text-[13vw] leading-none text-cream/[0.05] px-4 pt-10 whitespace-nowrap text-center">
      Why Not
    </p>
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-8 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
      <div>
        <Logo dark />
        <p className="mt-5 text-sm text-cream/60 leading-relaxed">
          B2B export house for millets, ancient grains, pulses, seeds &amp; superfoods — bulk &amp; private label.
        </p>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/40">{BRAND.coords}</p>
      </div>
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-leaf mb-5">Explore</p>
        <ul className="space-y-3 text-sm">
          {NAV.slice(1).map((l) => (
            <li key={l.href}>
              <Link to={l.href} data-testid={`footer-nav-${l.name.toLowerCase().replace(/\s+/g, "-")}`} className="text-cream/70 hover:text-cream transition-colors">{l.name}</Link>
            </li>
          ))}
          <li><Link to="/track" data-testid="footer-nav-track" className="text-cream/70 hover:text-cream transition-colors">Track Shipment</Link></li>
        </ul>
      </div>
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-leaf mb-5">Trade Desk</p>
        <ul className="space-y-3 text-sm text-cream/70">
          <li><Link to="/request-quote" data-testid="footer-quote-link" className="hover:text-cream transition-colors">Request a Quote</Link></li>
          <li><Link to="/export-process" data-testid="footer-terms-link" className="hover:text-cream transition-colors">Incoterms &amp; Payment Terms</Link></li>
          <li><Link to="/certifications" data-testid="footer-documents-link" className="hover:text-cream transition-colors">Export Documentation</Link></li>
          <li><a href={WA_LINK()} data-testid="footer-whatsapp-link" target="_blank" rel="noopener noreferrer" className="hover:text-cream transition-colors">WhatsApp Export Desk</a></li>
        </ul>
      </div>
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-leaf mb-5">Contact</p>
        <ul className="space-y-3 text-sm text-cream/70" data-testid="footer-contact">
          <li className="flex items-start gap-2.5"><Mail className="h-4 w-4 mt-0.5 text-tang shrink-0" />{CONTACT.email}</li>
          <li className="flex items-start gap-2.5"><Phone className="h-4 w-4 mt-0.5 text-tang shrink-0" />{CONTACT.phoneDisplay}</li>
          <li className="flex items-start gap-2.5"><MapPin className="h-4 w-4 mt-0.5 text-tang shrink-0" />{CONTACT.address}<br /><span className="text-cream/40 text-xs">[{CONTACT.addressNote}]</span></li>
        </ul>
      </div>
    </div>
    <div className="relative border-t border-cream/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] font-mono uppercase tracking-[0.15em] text-cream/40">
        <p>© {new Date().getFullYear()} {BRAND.name} · {BRAND.tagline}</p>
        <p className="text-center md:text-right">
          Enquiries are stored only to respond to your request ·
          <a href="/sitemap.xml" data-testid="footer-sitemap-link" className="mx-1.5 hover:text-cream">Sitemap</a>·
          <a href="/robots.txt" data-testid="footer-robots-link" className="mx-1.5 hover:text-cream">Robots</a>
        </p>
      </div>
    </div>
  </footer>
);

export default function Layout() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="grain min-h-screen flex flex-col">
      <SmoothScroll />
      <Header />
      <main className="flex-1" data-testid="page-main">
        <Suspense key={location.pathname} fallback={<div role="status" aria-live="polite" data-testid="page-loading" className="min-h-[60vh] flex items-center justify-center text-forest font-mono text-sm">Loading…</div>}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster richColors position="top-center" toastOptions={{ style: { borderRadius: 0 } }} />
    </div>
  );
}
